/**
 * Shared column-type classification and value formatting for the widget wizard
 * (column chooser + data-sample preview). SQL type names are translated to the
 * plain-language vocabulary shown to end users: Text, Number, Date, Yes/No.
 */
export const NUMERIC_SQL_TYPES = Object.freeze([
  'int', 'bigint', 'smallint', 'tinyint', 'decimal', 'numeric', 'float', 'real', 'money', 'smallmoney',
]);

export const DATE_SQL_TYPES = Object.freeze([
  'date', 'datetime', 'datetime2', 'smalldatetime', 'datetimeoffset', 'time',
]);

export const FRIENDLY_TYPES = Object.freeze({
  text: Object.freeze({ key: 'text', label: 'Text', icon: 'mdi-format-text' }),
  number: Object.freeze({ key: 'number', label: 'Number', icon: 'mdi-numeric' }),
  date: Object.freeze({ key: 'date', label: 'Date', icon: 'mdi-calendar' }),
  bool: Object.freeze({ key: 'bool', label: 'Yes/No', icon: 'mdi-toggle-switch' }),
});

export const FRIENDLY_TYPE_ORDER = Object.freeze(['text', 'number', 'date', 'bool']);

/** SQL dataType → friendly type descriptor ({ key, label, icon }); unknown → Text. */
export function friendlyType(dataType) {
  const t = (dataType || '').toLowerCase();
  if (NUMERIC_SQL_TYPES.includes(t)) return FRIENDLY_TYPES.number;
  if (DATE_SQL_TYPES.includes(t)) return FRIENDLY_TYPES.date;
  if (t === 'bit') return FRIENDLY_TYPES.bool;
  return FRIENDLY_TYPES.text;
}

/** Standard 8-4-4-4-12 GUID; register key columns are nvarchar, so detection is value-based. */
export const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isGuidValue(value) {
  return typeof value === 'string' && GUID_REGEX.test(value.trim());
}

/** `0451d98a-…-0992e114` → `0451d98a…e114`: both ends survive so rows stay distinguishable. */
export function midTruncate(value) {
  const s = String(value);
  if (s.length <= 14) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
}

/** ISO datetime → `dd/mm/yyyy hh:mm`; anything unparseable passes through untouched. */
export function formatDateValue(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
